/**
 * Engagement step: signature must be a non-empty image data URL with real drawn ink
 * (enforced on canvas before save; format check mirrors backend acceptance).
 */

const SIGNATURE_DATA_URL_PREFIX = /^data:image\/(png|jpeg|jpg|webp);base64,/i;

/** True if the string looks like a supported non-empty base64 image data URL. */
export function isEngagementSignatureDataUrlValid(raw: string): boolean {
  const s = String(raw ?? "").trim();
  if (s.length < 120) return false;
  if (!SIGNATURE_DATA_URL_PREFIX.test(s)) return false;
  const i = s.indexOf(",");
  if (i < 0 || i >= s.length - 1) return false;
  const b64 = s.slice(i + 1).replace(/\s/g, "");
  if (b64.length < 80) return false;
  return /^[A-Za-z0-9+/]+=*$/.test(b64);
}

/**
 * Detect non-blank signature strokes on a canvas (opaque, non-near-white pixels).
 * Tuned for slate-coloured ink (#1e293b) on a clear/white background.
 */
export function canvasHasSignatureInk(canvas: HTMLCanvasElement): boolean {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return false;
  const w = canvas.width;
  const h = canvas.height;
  if (w < 1 || h < 1) return false;
  let ink = 0;
  const { data } = ctx.getImageData(0, 0, w, h);
  const minInk = Math.max(48, Math.floor((w * h) / 45_000));
  for (let p = 0; p < data.length; p += 4) {
    const a = data[p + 3];
    if (a < 28) continue;
    const sum = data[p] + data[p + 1] + data[p + 2];
    if (sum < 650) ink++;
    if (ink >= minInk) return true;
  }
  return false;
}
